import { httpClient } from '@shared/infrastructure/http/http-client.js';
import type { DetectionFeedback, FeedbackDraft } from '../model.js';

interface FeedbackPayload {
  id: number;
  run_id: string;
  target_type: DetectionFeedback['targetType'];
  target_ref: string;
  verdict: DetectionFeedback['verdict'];
  gesture_name?: string | null;
  start_ms?: number | null;
  end_ms?: number | null;
  error_type?: DetectionFeedback['errorType'] | null;
  cause?: DetectionFeedback['cause'] | null;
  expected_timestamp_ms?: number | null;
  note?: string | null;
  idea_id?: string | null;
  created_at?: string | null;
}

function toFeedback(payload: FeedbackPayload): DetectionFeedback {
  return {
    id: payload.id,
    runId: payload.run_id,
    targetType: payload.target_type,
    targetRef: payload.target_ref,
    verdict: payload.verdict,
    gestureName: payload.gesture_name ?? undefined,
    startMs: payload.start_ms ?? undefined,
    endMs: payload.end_ms ?? undefined,
    errorType: payload.error_type ?? undefined,
    cause: payload.cause ?? undefined,
    expectedTimestampMs: payload.expected_timestamp_ms ?? undefined,
    note: payload.note ?? undefined,
    ideaId: payload.idea_id ?? undefined,
    createdAt: payload.created_at ? new Date(payload.created_at) : null,
  };
}

function draftBody(draft: FeedbackDraft) {
  return {
    runId: draft.runId,
    targetType: draft.targetType,
    targetRef: draft.targetRef,
    verdict: draft.verdict,
    gestureName: draft.gestureName,
    startMs: draft.startMs,
    endMs: draft.endMs,
    errorType: draft.errorType,
    cause: draft.cause,
    expectedTimestampMs: draft.expectedTimestampMs,
    note: draft.note,
    ideaId: draft.ideaId,
  };
}

/**
 * Boundary with the judgements about an analysis.
 *
 * The run id travels on every write, which is the one thing the legacy gets wrong here.
 */
export const detectionFeedbackApi = {
  list: async (videoId: number, runId?: string): Promise<DetectionFeedback[]> => {
    const rows = await httpClient.get<FeedbackPayload[]>(
      `/virtual-profiler/videos/${videoId}/feedback${runId ? `?run=${encodeURIComponent(runId)}` : ''}`,
    );

    return (rows ?? []).map(toFeedback);
  },

  create: async (draft: FeedbackDraft): Promise<DetectionFeedback> =>
    toFeedback(
      await httpClient.post<FeedbackPayload>(
        `/virtual-profiler/videos/${draft.videoId}/feedback`,
        draftBody(draft),
      ),
    ),

  /**
   * Several judgements of one video in one write (2026-10-29): an idea's verdict and its alerts
   * together. One invalid item writes none.
   */
  createMany: async (
    videoId: number,
    drafts: readonly FeedbackDraft[],
  ): Promise<DetectionFeedback[]> =>
    (
      (await httpClient.post<FeedbackPayload[]>(`/virtual-profiler/videos/${videoId}/feedback/bulk`, {
        items: drafts.map(draftBody),
      })) ?? []
    ).map(toFeedback),

  remove: (videoId: number, feedbackId: number): Promise<void> =>
    httpClient.delete<void>(`/virtual-profiler/videos/${videoId}/feedback/${feedbackId}`),
};
